import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import ElectricityHeatSteamEmissionsDisplay from '/business/shared/components/questions/scope2/_education/ElectricityHeatSteamEmissionsDisplay';

export const BottomHeatSteamLandingPage = (
  <>
    <div className="hse-landingbtm">
      <StyledHeading as="h2" $type="h1" $hasExtraBottomMargin $hasSuperTopMargin>
        Inputting data for this category
      </StyledHeading>
      <StyledParagraph $largeText>
        You will need to provide details on your locations, consumption data, and any energy attribute certificates
        associated with them.
      </StyledParagraph>
      <StyledHeading as="h2" $type="h1" $hasExtraBottomMargin $hasSuperTopMargin>
        Location data
      </StyledHeading>
      <StyledParagraph $largeText>For each location:</StyledParagraph>
      <ul>
        <li>
          <StyledParagraph $largeText>
            Indicate whether your business occupies the entire building or shares it with others.
          </StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>
            Specify whether energy use is measured by a <b>dedicated or shared meter</b>.
          </StyledParagraph>
        </li>
      </ul>
      <StyledParagraph $largeText>
        Combining this information with consumption data and energy attribute certificates (EACs) will provide a
        clearer, location-specific understanding of your business’s energy use and emissions.
      </StyledParagraph>

      <StyledHeading as="h2" $type="h1" $hasExtraBottomMargin $hasSuperTopMargin>
        Consumption data
      </StyledHeading>
      <StyledParagraph $largeText>For each defined location, enter:</StyledParagraph>
      <ul>
        <li>
          <StyledParagraph $largeText>The type of energy consumed (electricity, heat, or steam).</StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>
            The <b>amount used</b>, based on either activity or supplier intensity data.
          </StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>
            Any <b>energy attribute certificates</b> applied to that location.
          </StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>
            <b>Activity Data:</b>
            <br />
            Total kWh of electricity, heat, or steam consumed at the selected location.
          </StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>
            <b>Supplier Intensity Data:</b>
            <br />
            Carbon intensity information (e.g., kg CO₂e per kWh or per product/service unit) obtained directly from your
            energy supplier, in line with the GHG Protocol’s preference for supplier-specific data.
          </StyledParagraph>
        </li>
      </ul>
      <StyledParagraph $largeText>
        <strong>(Optional)</strong> On-site renewables
      </StyledParagraph>

      <StyledParagraph $largeText>
        Enter the amount of electricity consumed from <b>on-site renewable sources</b> (e.g., solar panels) that was
        used directly by your business during the reporting period, rather than purchased through a renewable energy
        contract such as a PPA, REGO, or GO.
      </StyledParagraph>

      <StyledHeading as="h2" $type="h1" $hasExtraBottomMargin $hasSuperTopMargin>
        Energy attribute certificates
      </StyledHeading>
      <StyledParagraph $largeText>
        An <b>Energy Attribute Certificate (EAC)</b> is a tradable certificate that confirms that one megawatt-hour
        (MWh) of electricity was generated from a specific source, typically renewable energy.
      </StyledParagraph>
      <StyledParagraph $largeText>
        EACs serve as proof that renewable energy has been produced and injected into the grid, allowing organisations
        and individuals to claim the associated benefits, such as reduced greenhouse gas emissions, without physically
        consuming the electricity from that source.
      </StyledParagraph>
      <StyledParagraph $largeText>
        EACs are an umbrella term covering regional and international schemes, including:
      </StyledParagraph>
      <ul>
        <li>
          <StyledParagraph $largeText>
            <b>Renewable Energy Certificates (RECs)</b> in North America
          </StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>
            <b>Guarantees of Origin (GOs)</b> in Europe
          </StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>
            <b>International RECs (I-RECs)</b> in global markets
          </StyledParagraph>
        </li>
      </ul>
      <StyledParagraph $largeText>
        <b>Certificate details may include:</b>
      </StyledParagraph>
      <ul>
        <li>
          <StyledParagraph $largeText>Total kWh covered by the certificate</StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>Remaining kWh available</StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>kWh allocated for this carbon review</StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>Supplier carbon intensity (e.g. kg CO₂e/kWh)</StyledParagraph>
        </li>
        <li>
          <StyledParagraph $largeText>Certificate validity period</StyledParagraph>
        </li>
      </ul>
      <ElectricityHeatSteamEmissionsDisplay />
    </div>
  </>
);
